/**
 * 🚨 SISTEMA DE MANEJO GLOBAL DE ERRORES
 * Captura y maneja errores críticos de JavaScript
 */

// 1️⃣ Suprimir ResizeObserver loop error (80% de los errores)
const suppressResizeObserverError = () => {
    const resizeObserverErr = /^[^(ResizeObserver loop limit exceeded)]/;
    window.addEventListener('error', (e: ErrorEvent) => {
        if (resizeObserverErr.test(e.message)) {
            const resizeObserverErrDiv = document.getElementById(
                'webpack-dev-server-client-overlay-div'
            );
            const resizeObserverErr = document.getElementById(
                'webpack-dev-server-client-overlay'
            );
            if (resizeObserverErr) {
                resizeObserverErr.setAttribute('style', 'display: none');
            }
            if (resizeObserverErrDiv) {
                resizeObserverErrDiv.setAttribute('style', 'display: none');
            }
        }
    });

    // Capturar y silenciar error de ResizeObserver
    const originalError = console.error;
    console.error = (...args: any[]) => {
        if (
            typeof args[0] === 'string' &&
            args[0].includes('ResizeObserver loop')
        ) {
            return; // Silenciar este error específico
        }
        originalError.apply(console, args);
    };
};

// 2️⃣ Proteger contra errores de window.webkit (13.33% de los errores)
const protectWebkitCalls = () => {
    if (typeof window !== 'undefined') {
        // Crear objeto webkit vacío si no existe (previene crashes en WebView)
        if (!(window as any).webkit) {
            (window as any).webkit = {
                messageHandlers: {}
            };
        }
    }
};

// 3️⃣ Manejo global de errores no capturados
const setupGlobalErrorHandling = () => {
    // Errores JavaScript no capturados
    window.addEventListener('error', (event: ErrorEvent) => {
        // Ignorar errores de ResizeObserver
        if (event.message.includes('ResizeObserver loop')) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        // Log estructurado para debugging
        if (import.meta.env.DEV) {
            console.error('🚨 Error capturado:', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                error: event.error
            });
        }

        // En producción, enviar a servicio de logging (Sentry, LogRocket, etc.)
        // trackError(event);
    });

    // Promesas rechazadas no manejadas
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        if (import.meta.env.DEV) {
            console.error('🚨 Promise rechazada:', event.reason);
        }

        // En producción, enviar a servicio de logging
        // trackError(event.reason);
    });
};

// 4️⃣ Proteger accesos a propiedades undefined
export const safeGetProperty = <T extends Record<string, any>, K extends keyof T>(
    obj: T | null | undefined,
    key: K,
    defaultValue?: T[K]
): T[K] | undefined => {
    try {
        return obj?.[key] ?? defaultValue;
    } catch (error) {
        console.warn(`⚠️ Error accediendo a propiedad "${String(key)}":`, error);
        return defaultValue;
    }
};

// 5️⃣ Wrapper seguro para operaciones con .valor
export const safeValor = (
    obj: any,
    defaultValue: number = 0
): number => {
    if (!obj || typeof obj !== 'object') {
        console.warn('⚠️ Objeto inválido en safeValor:', obj);
        return defaultValue;
    }
    
    if (typeof obj.valor === 'number') {
        return obj.valor;
    }
    
    console.warn('⚠️ Propiedad .valor no encontrada o inválida:', obj);
    return defaultValue;
};

// 6️⃣ Inicialización del sistema de errores
export const initErrorHandler = () => {
    suppressResizeObserverError();
    protectWebkitCalls();
    setupGlobalErrorHandling();
};

export default {
    init: initErrorHandler,
    safeGetProperty,
    safeValor
};
